import React, { useState } from "react";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { Card, FormGroup, showSnackbar, Table } from "@cofano/cck";

import { Alert } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCube } from "@fortawesome/pro-duotone-svg-icons";

import StatusChanger from "view/statuses/components/changer/StatusChanger";
import { useConfirm } from "components/hooks/useConfirm";

import { Fields, hasOptionalFieldPermission, hasPluginPermission, Language, usePermission } from "imports/Utils";

import CargoCreateEditDialog, { CargoDialogType } from "view/cargos/components/dialogs/CargoCreateEditDialog";

import { DetailsBlock, Height } from "../Blocks";

import { gql } from "__generated__/gql";
import { Cargo, CargoAction, SalesCharter } from "types/index";
import { useFrontendTable } from "components/table";

import { LocationLink, ProductLink } from "utils/LinkUtils";
import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";
import ConnectingVisitField from "view/cargos/components/connectingVisits/ConnectingVisitField";
import CargoServiceIcons from "view/cargos/components/CargoServiceIcons";
import { DocumentEntity, DocumentType, StatusType } from "__generated__/graphql";
import DocumentUploadDialog from "components/documents/DocumentUploadDialog";
import { cargoHasServices } from "view/cargos/utils/CargoUtils";
import { FormattedDate } from "utils/DateUtils";
import { getCargoBlDate } from "utils/cargoActionUtils";
import CargoQuantity from "view/cargos/components/quantities/CargoQuantity";
import CargoActionActualsQuantity from "view/cargos/components/quantities/CargoActionActualsQuantity";
import { ShipToShipPopover } from "view/charters/components/popover/ShipToShipPopover";
import { TransportDocumentMenu } from "view/cargos/components/download/TransportDocumentPdfButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import EntityHistoryDialog from "components/entityHistory/components/dialogs/EntityHistoryDialog";
import { HistoryEntity } from "__generated__/enums";
import HistoryIcon from "@mui/icons-material/History";

type CargoDetailsBlockProps = {
	charter: SalesCharter,
	color: string;
	refetch: () => void;
	openEmailDialog?: () => void;
};

const CargoDetailsBlock = ({ charter, color, refetch, openEmailDialog }: CargoDetailsBlockProps) => {

	const session = useStoreState(store => store.session);

	const confirm = useConfirm();
	const { isReadOnly, isRTR } = usePermission();

	const [ selectedRowData, setSelectedRowData ] = useState<Cargo | undefined>(undefined);
	const [ documentDialogOpen, setDocumentDialogOpen ] = useState(false);
	const [ pdfMenu, setPdfMenu ] = useState<{ anchorEl: HTMLElement | null, cargo: Cargo | null }>({ anchorEl: null, cargo: null });

	const cargos = (charter.cargos || [])
		.map((cargo, index) => ({
			...cargo,
			cargoNumber: cargo.cargoNumber || String(index + 1),
		}));

	const { tableProps  } = useFrontendTable({
		id: "salesCharterCargos",
		content: cargos.sort((a, b) => (a.cargoNumber ?? a.id.toString()).localeCompare(b.cargoNumber ?? b.id.toString())),
		saveForOrganizationEnabled: false,
	});

	const params = new URLSearchParams(window.location.search);

	const [ createEditDialog, setCreateEditDialog ] = useState<{ open: boolean, cargoId: number | undefined }>({
		open: params.has("edit-cargo"),
		cargoId: Number(params.get("edit-cargo")),
	});

	const [ historyDialogCargo, setHistoryDialogCargo ] = useState<{
		id: number;
		cargoNumber: string | null | undefined
	} | null>(null);

	const [ deleteCargo ] = useMutation(DELETE_CARGO);
	const [ duplicateCargo ] = useMutation(DUPLICATE_CARGO);
	const [ deleteDocument ] = useMutation(deleteCustomerTransportDocument_mutation);

	const onDeleteDocument = (cargo: Cargo) => {
		if (cargo.customerTransportDocument == null) {
			return;
		}

		confirm({ type: "DELETE" })
			.then(() => {
				deleteDocument({
					variables: {
						cargoId: cargo.id,
						documentId: cargo.customerTransportDocument!.id,
					},
				}).then(() => refetch());
			});
	};

	const clearParam = (param: string) => {
		const params = new URLSearchParams(window.location.search);
		params.delete(param);
		window.history.replaceState(null, "", location.origin + location.pathname + "?" + params.toString());
	};

	const setParam = (param: string, value: string) => {
		const params = new URLSearchParams(window.location.search);
		params.set(param, value);
		window.history.replaceState(null, "", location.origin + location.pathname + "?" + params.toString());
	};

	return (
		<React.Fragment>
			<DetailsBlock height={Height.Fill}>
				<Card
					color={color}
					icon={<FontAwesomeIcon icon={faCube} style={{ color: color, height: 22, width: 22 }} />}
					title={intl.formatMessage({ id: "CARGOS" })}
					actions={[ {
						icon: <AddIcon fontSize="small" />,
						hidden: isReadOnly || !isRTR,
						onClick: () => setCreateEditDialog({ open: true, cargoId: undefined }),
					} ]}
					disablePadding>
					<React.Fragment>
						{cargos.length === 0 ? (
							<div style={{ padding: 16 }}>
								<Alert severity="warning">
									{intl.formatMessage({ id: "NO_CARGOS_FOUND" })}
								</Alert>
							</div>
						) : (
							<div className={"cargos-table"}>
								<Table
									key={JSON.stringify(cargos)}
									{...tableProps}
									columns={[
										{
											id: "containerNumber",
											title: intl.formatMessage({ id: "CONTAINER_NUMBER" }),
											width: 150,
											disableFlexGrow: true,
											render: (item) => (
												<span style={{ fontWeight: 900 }}>
													{item.containerNumber}
												</span>
											),
											disabled: !hasPluginPermission(session, "useContainers"),
										},
										{
											id: "cargoNumber",
											render: (item) => (
												<span style={{ fontWeight: 900 }}>
													{`${intl.formatMessage({ id: "CARGO" })} ${item.cargoNumber}`}
												</span>
											),
											title: intl.formatMessage({ id: "CARGO" }),
											disableFlexGrow: true,
											width: 100,
											disabled: hasPluginPermission(session, "useContainers"),
										},
										{
											id: "status",
											title: intl.formatMessage({ id: "STATUS" }),
											width: 180,
											disableFlexGrow: true,
											render: (item) =>
												<StatusChanger
													session={session}
													entity={item}
													statusType={StatusType.Cargo}
													refetch={refetch}
													showHistory
												/>
											,
										},
										{
											id: "product",
											title: intl.formatMessage({ id: "PRODUCT" }),
											width: 150,
											render: (item) => (
												<div className={"cargo-item-info"}>
													<FormGroup label={intl.formatMessage({ id: "PRODUCT" })}
														labelPlacement="left-center"
														labelWidth={80}
														overflow="hidden">
														<ProductLink product={item.product} />
													</FormGroup>
													<FormGroup label={Language("QUANTITY")}
														labelPlacement="left-center"
														labelWidth={80}
														overflow="hidden">
														<CargoQuantity cargo={item} />
													</FormGroup>
												</div>
											),
											disabled: hasPluginPermission(session, "useContainers"),
										},
										{
											id: "services",
											title: intl.formatMessage({ id: "CARGO_SERVICES" }),
											disableFlexGrow: true,
											width: 180,
											render: (item) => <CargoServiceIcons cargo={item} />,
											disabled: charter.cargos?.filter(cargoHasServices).length === 0,
										},
										{
											id: "location",
											title: intl.formatMessage({ id: "LOCATION" }),
											disableFlexGrow: false,
											width: 250,
											render: (item) => (
												<div>
													<FormGroup label={intl.formatMessage({ id: "LOADING" })}
														labelPlacement="left-center"
														labelWidth={100}
														overflow="hidden">
														<div style={{ display: "flex", alignItems: "center" }}>
															<span style={{ textOverflow: "ellipsis", overflow: "hidden", maxWidth: "80%" }}>
																<LocationLink location={item.firstLoadingAction?.location} />
															</span>
															{item.firstLoadingAction?.connectingShipPortVisit ?  (
																<ConnectingVisitField action={item.firstLoadingAction} refetch={refetch} />
															) : item.firstLoadingAction?.shipToShip ? (
																<ShipToShipPopover />
															) : null}
														</div>
													</FormGroup>

													<FormGroup label={intl.formatMessage({ id: "DISCHARGING" })}
														labelPlacement="left-center"
														labelWidth={100}
														overflow="hidden">
														<div style={{ display: "flex", alignItems: "center" }}>
															<span style={{ textOverflow: "ellipsis", maxWidth: "80%"  }}>
																<LocationLink location={item.lastDischargingAction?.location} />
															</span>
															{item.lastDischargingAction?.connectingShipPortVisit ?  (
																<ConnectingVisitField action={item.lastDischargingAction} refetch={refetch} />
															) : item.lastDischargingAction?.shipToShip ? (
																<ShipToShipPopover />
															) : null}
														</div>
													</FormGroup>
												</div>
											),
										},
										{
											id: "actionDate",
											title: intl.formatMessage({ id: "PLANNED_DATE" }),
											disableFlexGrow: false,
											width: 250,
											render: (item) => (
												<div className={"cargo-item-info"}>
													<FormGroup label={intl.formatMessage({ id: "LOADING" })}
														labelPlacement="left-center"
														labelWidth={100}
														overflow="hidden">
														<CargoActionDate action={item.firstLoadingAction} />
													</FormGroup>
													<FormGroup label={Language("DISCHARGING")}
														labelPlacement="left-center"
														labelWidth={100}
														overflow="hidden">
														<CargoActionDate action={item.lastDischargingAction} />
													</FormGroup>
												</div>
											),
										},
										{
											id: "bl",
											title: intl.formatMessage({ id: "BL" }),
											disableFlexGrow: false,
											width: 150,
											render: (item) => {
												const actions = item.actions as CargoAction[] ?? [];
												const blDate = getCargoBlDate(actions);

												return (
													<div>
														<FormGroup
															label={Language("DATE")}
															labelPlacement="left-center"
															labelWidth={70}
															overflow="hidden">
															{blDate ? (
																<FormattedDate date={blDate} format="ll" />
															) : (
																"-"
															)}
														</FormGroup>
														<FormGroup
															label={Language("QUANTITY")}
															labelPlacement="left-center"
															labelWidth={70}
															overflow="hidden">
															<CargoActionActualsQuantity
																measurements={item.firstLoadingAction.measurements}
																cargo={item}
																convertActualsToMton
															/>
														</FormGroup>
													</div>
												);
											},
										},
										{
											id: "deliveryCondition",
											title: intl.formatMessage({ id: "DELIVERY_CONDITION" }),
											sortable: true,
											disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "deliveryCondition"),
											defaultVisible: true,
											render: (row) => row.deliveryCondition?.description || "",
										},
									]}
									rowActions={[
										{
											icon: <InsertDriveFileIcon />,
											tooltip: intl.formatMessage({ id: "TRANSPORT_DOCUMENT" }),
											onClick: (row, event) => setPdfMenu({ anchorEl: event.currentTarget, cargo: row }),
										}, {
											icon: <ContentCopyIcon />,
											tooltip: intl.formatMessage({ id: "DUPLICATE" }),
											onClick: (row: Cargo) => {
												duplicateCargo({
													variables: { cargoId: row.id },
												}).then(() => {
													showSnackbar(intl.formatMessage({ id: "DUPLICATE_CARGO_SUCCESS" }), { variant: "success" });
													refetch();
												});
											},
											disabled: isReadOnly,
										}, {
											icon: <EditIcon />,
											tooltip: intl.formatMessage({ id: "EDIT" }),
											onClick: (row) => {
												setCreateEditDialog({
													open: true,
													cargoId: row.id,
												});
												setParam("edit-cargo", String(row.id));
											},
											disabled: isReadOnly,
										}, {
											icon: <HistoryIcon />,
											tooltip: intl.formatMessage({ id: "HISTORY" }),
											onClick: (row) => setHistoryDialogCargo(row),
											disabled: isReadOnly,
										},
										{
											icon: <DeleteIcon />,
											tooltip: intl.formatMessage({ id: "DELETE" }),
											onClick: (row) => {
												confirm({
													type: "DELETE",
												}).then(() => {
													deleteCargo({
														variables: {
															id: row.id,
															deleteEmptyVoyage: true,
														},
													}).then(() => {
														refetch();
													});
												});
											},
											disabled: isReadOnly,
										} ]}
								/>
							</div>
						)}
					</React.Fragment>
				</Card>
			</DetailsBlock>

			<CargoCreateEditDialog
				open={createEditDialog.open}
				onClose={(shouldOpenEmailDialog) => {
					setCreateEditDialog({ open: false, cargoId: undefined });
					clearParam("edit-cargo");
					if (shouldOpenEmailDialog) {
						openEmailDialog?.();
					}
				}}
				charter={charter}
				cargoId={createEditDialog.cargoId}
				refetch={refetch}
				type={CargoDialogType.CUSTOM}
				validFrom={charter.contract?.validFrom}
				validUntil={charter.contract?.validUntil}
			/>

			{pdfMenu.cargo && (
				<TransportDocumentMenu
					cargo={pdfMenu.cargo}
					anchorEl={pdfMenu.anchorEl}
					onClose={() => setPdfMenu((prev) => ({ ...prev, anchorEl: null }))}
					onAddCustomerDocument={() => {
						setSelectedRowData(pdfMenu.cargo ?? undefined);
						setDocumentDialogOpen(true);
					}}
					onRemoveCustomerDocument={() => {
						if (pdfMenu.cargo) {
							onDeleteDocument(pdfMenu.cargo);
						}
					}}
				/>
			)}

			{historyDialogCargo && (
				<EntityHistoryDialog
					open={true}
					onClose={() => setHistoryDialogCargo(null)}
					entity={HistoryEntity.Cargo}
					entityId={historyDialogCargo.id}
					title={intl.formatMessage({ id: "HISTORY_FOR_CARGO" }, {
						cargoNumber: historyDialogCargo.cargoNumber,
					})}
				/>
			)}

			{selectedRowData && (
			<DocumentUploadDialog
				defaultDocumentType={DocumentType.CustomerTransport}
				open={documentDialogOpen}
				onClose={() => setDocumentDialogOpen(false)}
				entities={[ {
					id: selectedRowData.id,
					name: DocumentEntity.Cargo,
					type: DocumentEntity.Cargo,
				} ]}
				onUploaded={refetch} />
		)}
		</React.Fragment>
	);
};

const DELETE_CARGO = gql(`#graphql
	mutation deleteCargo_cargoBlock($id: Long!, $deleteEmptyVoyage: Boolean = true) {
		deleteCargo(id: $id, deleteEmptyVoyage: $deleteEmptyVoyage)
	}
`);

const deleteCustomerTransportDocument_mutation = gql(`#graphql
	mutation deleteCustomerTransportDocument_cargoBlock($cargoId: Long!, $documentId: Long!) {
		deleteCustomerTransportDocument(cargoId: $cargoId, documentId: $documentId)
	}
`);

const DUPLICATE_CARGO = gql(`#graphql
	mutation duplicateCargo_cargoBlock($cargoId: Long!) {
		duplicateCargo(cargoId: $cargoId) {
			id
		}
	}
`);

export default CargoDetailsBlock;
